import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Plus, Save, Trash2, Search, History, Carrot, FileDown, Pencil } from "lucide-react";

const EMPTY = { name: "", unit: "kg", price_per_unit: "", price_target: "", supplier: "", category: "", waste_pct: "", notes: "", count_in_stock: true };

export default function Ingredients() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [history, setHistory] = useState(null);
  const [catFilter, setCatFilter] = useState("all");
  const [supFilter, setSupFilter] = useState("all");

  const { data: meta } = useQuery({ queryKey: ["recipes-meta"], queryFn: async () => (await api.get("/recipes/meta")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["ingredients", q],
    queryFn: async () => (await api.get(`/recipes/ingredients?q=${encodeURIComponent(q)}`)).data,
  });

  const save = useMutation({
    mutationFn: async (row) => {
      const body = {
        name: row.name, unit: row.unit, price_per_unit: Number(row.price_per_unit) || 0,
        price_target: Number(row.price_target) || 0,
        supplier: row.supplier || "", category: row.category || "",
        waste_pct: Number(row.waste_pct) || 0, notes: row.notes || "",
        count_in_stock: row.count_in_stock !== false,
      };
      return row.id
        ? (await api.put(`/recipes/ingredients/${row.id}`, body)).data
        : (await api.post("/recipes/ingredients", body)).data;
    },
    onSuccess: () => {
      setEdit(null);
      qc.invalidateQueries({ queryKey: ["ingredients"] });
      qc.invalidateQueries({ queryKey: ["recipes"] });
      toast.success("Το υλικό αποθηκεύτηκε");
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/recipes/ingredients/${id}`)).data,
    onSuccess: () => { setEdit(null); qc.invalidateQueries({ queryKey: ["ingredients"] }); toast.success("Το υλικό διαγράφηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const toggleCount = useMutation({
    mutationFn: async (r) => (await api.put(`/recipes/ingredients/${r.id}`, {
      name: r.name, unit: r.unit, price_per_unit: Number(r.price_per_unit) || 0,
      price_target: Number(r.price_target) || 0, supplier: r.supplier || "", category: r.category || "",
      waste_pct: Number(r.waste_pct) || 0, notes: r.notes || "", count_in_stock: !(r.count_in_stock !== false),
    })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ingredients"] }),
    onError: (e) => toast.error(apiErr(e)),
  });

  const downloadList = async () => {
    try {
      const res = await api.get("/procurement/prices/template.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "TAF_Lista_Proionton.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const rows = data?.rows || [];
  const cats = useMemo(() => [...new Set(rows.map((r) => r.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "el")), [rows]);
  const sups = useMemo(() => [...new Set(rows.map((r) => r.supplier).filter(Boolean))].sort((a, b) => a.localeCompare(b, "el")), [rows]);
  const filtered = rows.filter((r) => (catFilter === "all" || r.category === catFilter) && (supFilter === "all" || r.supplier === supFilter));

  const quickSave = useMutation({
    mutationFn: async (r) => (await api.put(`/recipes/ingredients/${r.id}`, {
      name: r.name, unit: r.unit, price_per_unit: Number(r.price_per_unit) || 0, price_target: Number(r.price_target) || 0,
      supplier: r.supplier || "", category: r.category || "", waste_pct: Number(r.waste_pct) || 0,
      notes: r.notes || "", count_in_stock: r.count_in_stock !== false,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["ingredients"] }); toast.success("Αποθηκεύτηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const { data: hist } = useQuery({
    queryKey: ["ing-history", history],
    queryFn: async () => (await api.get(`/recipes/ingredients/${history}/history`)).data,
    enabled: !!history,
  });

  const field = "mt-1 w-full rounded-xl border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">F&amp;B · Τιμοκατάλογος</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Υλικά &amp; Τιμές</h1>
          <p className="text-[#6F6A60] text-sm mt-1">Η τιμή κάθε υλικού τροφοδοτεί αυτόματα το κόστος των συνταγών. Η φθορά (%) προσαυξάνει το κόστος.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="ing-download" onClick={downloadList}
            className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm font-semibold hover:border-[#B9AE9A]">
            <FileDown className="h-4 w-4" /> Λίστα προϊόντων (Excel)
          </button>
          <button data-testid="ing-new" onClick={() => setEdit({ ...EMPTY })}
            className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:opacity-90">
            <Plus className="h-4 w-4" /> Νέο υλικό
          </button>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] space-y-3">
          <div className="flex items-center gap-3">
            <Carrot className="h-4 w-4 text-[#C86235]" />
            <h2 className="font-medium text-[#45423C]">{filtered.length} / {rows.length} υλικά</h2>
            <select data-testid="ing-supplier-filter" value={supFilter} onChange={(e) => setSupFilter(e.target.value)}
              className="ml-auto rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm outline-none focus:border-[#45423C] max-w-[200px]">
              <option value="all">Όλοι οι προμηθευτές</option>
              {sups.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="relative">
              <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="ing-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση υλικού"
                className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
            </div>
          </div>
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid="ing-cat-tabs">
            <button data-testid="ing-cat-all" onClick={() => setCatFilter("all")}
              className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${catFilter === "all" ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`}>Όλες</button>
            {cats.map((c) => (
              <button key={c} data-testid={`ing-cat-${c}`} onClick={() => setCatFilter(c)}
                className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap transition-colors ${catFilter === c ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`}>{c}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Υλικό</th>
                <th className="px-3 py-3">Κατηγορία</th>
                <th className="px-3 py-3">Προμηθευτής</th>
                <th className="px-3 py-3">Περιγραφή / Σημείωση</th>
                <th className="px-3 py-3 text-center">Μονάδα</th>
                <th className="px-3 py-3 text-right">Τιμή / μονάδα</th>
                <th className="px-3 py-3 text-right">Στόχος</th>
                <th className="px-3 py-3 text-right">Φθορά</th>
                <th className="px-3 py-3 text-center">Απογραφή</th>
                <th className="px-3 py-3 text-right" />
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`ing-row-${r.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                  <td className="px-4 py-2.5 font-medium text-[#45423C]">
                    <button data-testid={`ing-edit-${r.id}`} onClick={() => setEdit({ ...r })} className="hover:underline">{r.name}</button>
                  </td>
                  <td className="px-3 py-2.5 text-[#6F6A60] text-xs">{r.category || "—"}</td>
                  <td className="px-3 py-2.5 text-[#6F6A60] text-xs">{r.supplier || "—"}</td>
                  <td className="px-3 py-2.5">
                    <input data-testid={`ing-inline-notes-${r.id}`} defaultValue={r.notes || ""} placeholder="—"
                      onBlur={(e) => { if ((e.target.value || "") !== (r.notes || "")) quickSave.mutate({ ...r, notes: e.target.value }); }}
                      className="w-full min-w-[160px] rounded-lg border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-2 py-1 text-sm outline-none bg-transparent" />
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs">{r.unit}</td>
                  <td className="px-3 py-2.5 text-right">
                    <input data-testid={`ing-inline-price-${r.id}`} type="number" step="0.01" defaultValue={r.price_per_unit || ""}
                      onBlur={(e) => { const v = e.target.value; if ((Number(v) || 0) !== (Number(r.price_per_unit) || 0)) quickSave.mutate({ ...r, price_per_unit: v }); }}
                      className="w-20 rounded-lg border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-2 py-1 text-sm text-right tabular outline-none bg-transparent font-semibold" />
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <input data-testid={`ing-inline-target-${r.id}`} type="number" step="0.01" defaultValue={r.price_target || ""} placeholder="—"
                      onBlur={(e) => { const v = e.target.value; if ((Number(v) || 0) !== (Number(r.price_target) || 0)) quickSave.mutate({ ...r, price_target: v }); }}
                      className="w-20 rounded-lg border border-transparent hover:border-[#E3DED4] focus:border-[#45423C] px-2 py-1 text-sm text-right tabular outline-none bg-transparent text-[#6F6A60]" />
                  </td>
                  <td className="px-3 py-2.5 text-right tabular text-[#6F6A60]">{r.waste_pct ? `${r.waste_pct}%` : "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    <input type="checkbox" data-testid={`ing-count-${r.id}`} checked={r.count_in_stock !== false}
                      onChange={() => toggleCount.mutate(r)} className="cursor-pointer accent-[#2E6F7A]" title="Μέτρηση στη μηνιαία απογραφή" />
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <button data-testid={`ing-history-${r.id}`} onClick={() => setHistory(r.id)} title="Ιστορικό τιμών"
                      className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]"><History className="h-3.5 w-3.5" /></button>
                    <button data-testid={`ing-row-edit-${r.id}`} onClick={() => setEdit({ ...r })} title="Επεξεργασία"
                      className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`ing-row-del-${r.id}`} onClick={() => { if (window.confirm(`Διαγραφή του «${r.name}»;`)) remove.mutate(r.id); }} title="Διαγραφή"
                      className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#FEF2F2] hover:text-[#991B1B]"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-[#6F6A60]">
                  Δεν υπάρχουν υλικά με αυτά τα κριτήρια.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="ing-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg mt-10 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4]">
              <h3 className="font-medium text-[#45423C]">{edit.id ? "Επεξεργασία υλικού" : "Νέο υλικό"}</h3>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Όνομα</label>
                <input data-testid="ing-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className={field} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Μονάδα τιμολόγησης</label>
                  <select data-testid="ing-unit" value={edit.unit} onChange={(e) => setEdit({ ...edit, unit: e.target.value })} className={field}>
                    {(meta?.units || ["kg"]).map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Τιμή / μονάδα (€)</label>
                  <input data-testid="ing-price" type="number" step="0.01" value={edit.price_per_unit}
                    onChange={(e) => setEdit({ ...edit, price_per_unit: e.target.value })} className={`${field} text-right tabular`} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Προμηθευτής</label>
                  <input data-testid="ing-supplier" value={edit.supplier || ""} onChange={(e) => setEdit({ ...edit, supplier: e.target.value })} className={field} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Κατηγορία</label>
                  <input data-testid="ing-category" value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className={field} placeholder="π.χ. Κρεατικά" />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Φθορά / απώλεια (%)</label>
                <input data-testid="ing-waste" type="number" value={edit.waste_pct || ""} onChange={(e) => setEdit({ ...edit, waste_pct: e.target.value })} className={`${field} text-right tabular`} placeholder="0" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Τιμή-στόχος (€)</label>
                  <input data-testid="ing-target" type="number" step="0.01" value={edit.price_target || ""} onChange={(e) => setEdit({ ...edit, price_target: e.target.value })} className={`${field} text-right tabular`} placeholder="0" />
                </div>
                <label className="flex items-end gap-2 pb-2 cursor-pointer">
                  <input type="checkbox" data-testid="ing-count-toggle" checked={edit.count_in_stock !== false} onChange={(e) => setEdit({ ...edit, count_in_stock: e.target.checked })} className="accent-[#2E6F7A] h-4 w-4" />
                  <span className="text-sm text-[#45423C]">Μέτρηση στη μηνιαία απογραφή</span>
                </label>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#6F6A60] font-semibold">Σημειώσεις (για προμηθευτές)</label>
                <textarea data-testid="ing-notes" value={edit.notes || ""} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} rows={2} className={field} placeholder="π.χ. συσκευασία, ποιότητα, προτίμηση μάρκας" />
              </div>
              <div className="flex justify-between pt-2">
                {edit.id ? (
                  <button data-testid="ing-delete" onClick={() => { if (window.confirm(`Διαγραφή του «${edit.name}»;`)) remove.mutate(edit.id); }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold text-[#991B1B] hover:bg-[#FEF2F2]">
                    <Trash2 className="h-4 w-4" /> Διαγραφή
                  </button>
                ) : <span />}
                <div className="flex gap-2">
                  <button data-testid="ing-cancel" onClick={() => setEdit(null)}
                    className="rounded-xl border border-[#E3DED4] px-4 py-2 text-sm font-semibold hover:border-[#B9AE9A]">Άκυρο</button>
                  <button data-testid="ing-save" onClick={() => save.mutate(edit)} disabled={!edit.name?.trim() || save.isPending}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-40">
                    <Save className="h-4 w-4" /> {save.isPending ? "Αποθήκευση…" : "Αποθήκευση"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {history && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4" data-testid="ing-history-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg mt-16 border border-[#E3DED4]">
            <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center justify-between">
              <h3 className="font-medium text-[#45423C]">Ιστορικό τιμών</h3>
              <button data-testid="ing-history-close" onClick={() => setHistory(null)} className="text-sm text-[#6F6A60] hover:underline">Κλείσιμο</button>
            </div>
            <div className="p-5 space-y-2 max-h-[60vh] overflow-y-auto">
              {(hist?.rows || []).length === 0 && <p className="text-sm text-[#6F6A60]">Δεν υπάρχουν αλλαγές τιμής.</p>}
              {(hist?.rows || []).map((h) => (
                <div key={h.id} className="flex items-center justify-between text-sm border-b border-[#F1EDE6] pb-2">
                  <span className="text-[#6F6A60]">{new Date(h.changed_at).toLocaleDateString("el-GR")} · {h.changed_by}</span>
                  <span className="tabular">{fmtEuroCents(h.old_price || 0)} → <strong>{fmtEuroCents(h.new_price || 0)}</strong></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
